import { Box, Card, styled, Button, TextField, MenuItem, Typography, CircularProgress, FormControl, InputLabel, Select, Pagination } from "@mui/material";
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import { useEffect, useState, useCallback, useMemo } from "react";
import FieldVisitTable from "page-sections/FieldVisit/FieldVisitTable";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { usePageTitle } from "contexts/PageTitleContext";
import { GetApp, ArrowForward, Search } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import FieldVisitExportModal from "page-sections/FieldVisit/FieldVisitExportModal";
// ─── CHANGE 1: Import buttonConfig ─────────────────────────────────────────────
import buttonConfig from "config/ConfigAccessButton";
// ─────────────────────────────────────────────────────────────────────────────

const FieldVisit = () => {
    const { setPageTitle } = usePageTitle();
    const navigate = useNavigate();
    const [fieldVisits, setFieldVisits] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [hasSearched, setHasSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [companyFilter, setCompanyFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [totalVisits, setTotalVisits] = useState(null);
    const [loadingDashboard, setLoadingDashboard] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        page_size: 10,
        total_items: 0,
        total_pages: 1,
        has_next: false,
        has_previous: false,
        next_page_number: null,
        previous_page_number: null,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const pageSizeOptions = [10, 25, 50, 100];

    useEffect(() => {
        setPageTitle('Field Visit');
    }, [setPageTitle]);

    // ─── CHANGE 2: Setup permissions from config ───────────────────────────────
    const fieldVisitConfig = buttonConfig["Field visit"] || {};
    const can = (action) => !!fieldVisitConfig[action];
    // ─────────────────────────────────────────────────────────────────────────

    const isCompanyUser = apiService.isCompanyUser();
    const companyId = apiService.getCompanyId();

    const fetchCompanies = useCallback(async () => {
        if (isCompanyUser) {
            const details = apiService.getCompanyDetails();
            const id = apiService.getCompanyId();
            const singleCompany = {
                id: id,
                name: details?.name || "My Company",
            };
            setCompanyOptions([singleCompany]);
            setCompanyFilter(id?.toString() || "");
            return;
        }

        setLoadingCompanies(true);
        try {
            const response = await apiService.get(API_ENDPOINTS.GET_company_choices);
            const options = response?.data?.Company || response?.data?.choices || response?.data || [];
            if (Array.isArray(options)) {
                setCompanyOptions(options);
            } else {
                setCompanyOptions([]);
            }
        } catch (error) {
            console.error("Error fetching companies:", error);
            setCompanyOptions([]);
        } finally {
            setLoadingCompanies(false);
        }
    }, [isCompanyUser]);

    const fetchCategories = useCallback(async (selectedCompanyId = null) => {
        setLoadingCategories(true);
        try {
            const targetCompanyId = selectedCompanyId || (isCompanyUser ? companyId : null);
            
            if (!targetCompanyId) {
                setCategories([]);
                return;
            }

            const userId = localStorage.getItem('user_id');
            const response = await apiService.get(`${API_ENDPOINTS.GET_product_category}?company=${targetCompanyId}&user_id=${userId}`);
            
            if (response?.success) {
                const categoryList = response.data?.ProductCategory || [];
                setCategories(categoryList);
            } else {
                setCategories([]);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            setCategories([]);
        } finally {
            setLoadingCategories(false);
        }
    }, [isCompanyUser, companyId]);

    const fetchFieldVisits = useCallback(async (selectedCompanyId = null, page = 1, pageSizeParam = 10, search = "") => {
        setLoading(true);
        try {
            let url = API_ENDPOINTS.GET_field_visit;
            const params = new URLSearchParams();
            params.set('page', String(page));
            params.set('page_size', String(pageSizeParam));
            const targetCompanyId = selectedCompanyId || (isCompanyUser ? companyId : null);
            if (targetCompanyId) params.set('company_id', targetCompanyId);
            
            if (search && search.trim()) {
                params.set('search', search.trim());
            }
            
            url += (url.includes('?') ? '&' : '?') + params.toString();

            const response = await apiService.get(url);

            if (response?.success && response?.data?.FieldVisit) {
                setFieldVisits(response.data.FieldVisit);
            } else {
                setFieldVisits([]);
            }
            const paginationData = response?.pagination || response?.data?.pagination || {};
            if (Object.keys(paginationData).length > 0) {
                setPagination({
                    current_page: paginationData.current_page ?? page,
                    page_size: paginationData.page_size ?? pageSizeParam,
                    total_items: paginationData.total_items ?? 0,
                    total_pages: paginationData.total_pages ?? 1,
                    has_next: paginationData.has_next ?? false,
                    has_previous: paginationData.has_previous ?? false,
                    next_page_number: paginationData.next_page_number ?? null,
                    previous_page_number: paginationData.previous_page_number ?? null,
                });
                setCurrentPage(paginationData.current_page ?? page);
                setPageSize(paginationData.page_size ?? pageSizeParam);
            } else {
                const list = response?.data?.FieldVisit || [];
                setPagination(prev => ({ ...prev, total_items: list.length, total_pages: 1, current_page: 1, page_size: pageSizeParam }));
            }
        } catch (error) {
            console.error("API error:", error);
            setFieldVisits([]);
        } finally {
            setLoading(false);
        }
    }, [isCompanyUser, companyId]);

    const handleFieldVisitPageChange = (_, value) => {
        setCurrentPage(value);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, value, pageSize, searchTerm);
    };

    const handleFieldVisitPageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPageSize(newSize);
        setCurrentPage(1);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, 1, newSize, searchTerm);
    };

    const handleSearch = () => {
        if (!searchValue.trim()) return;
        setSearchTerm(searchValue);
        setCurrentPage(1);
        setHasSearched(true);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, 1, pageSize, searchValue);
    };

    const handleClearSearch = () => {
        setSearchValue("");
        setSearchTerm("");
        setCurrentPage(1);
        setHasSearched(false);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, 1, pageSize, "");
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && searchValue.trim()) {
            handleSearch();
        }
    };

    useEffect(() => {
        if (searchValue === "" && searchTerm !== "") {
            handleClearSearch();
        }
    }, [searchValue]);

    const fetchDashboardData = useCallback(async () => {
        setLoadingDashboard(true);
        try {
            const today = new Date();
            const dateFrom = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
            const dateTo = new Date(today.getFullYear() + 1, 0, 31).toISOString().split('T')[0];
            
            const url = `${API_ENDPOINTS.GET_field_visit_dashboard}?user_type=agent`;
            const response = await apiService.get(url);
            
            if (response?.success && response?.data?.summary) {
                setTotalVisits(response.data.summary.total_visits);
            } else {
                setTotalVisits(null);
            }
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
            setTotalVisits(null);
        } finally {
            setLoadingDashboard(false);
        }
    }, []);

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    useEffect(() => {
        if (companyFilter || (isCompanyUser && companyId)) {
            const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
            fetchCategories(targetCompanyId);
            setCategoryFilter("");
        } else {
            setCategories([]);
            setCategoryFilter("");
        }
    }, [companyFilter, isCompanyUser, companyId, fetchCategories]);

    useEffect(() => {
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, currentPage, pageSize, searchTerm);
    }, [companyFilter, isCompanyUser, companyId, fetchFieldVisits]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    const handleCompanyFilterChange = (e) => {
        setCompanyFilter(e.target.value);
        setSearchTerm("");
        setSearchValue("");
        setHasSearched(false);
    };

    const handleCategoryFilterChange = (e) => {
        setCategoryFilter(e.target.value);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        if (targetCompanyId) {
            fetchFieldVisits(targetCompanyId, currentPage, pageSize, searchTerm);
        }
    };

    const handleExportClick = () => {
        setExportModalOpen(true);
    };

    const handleExportModalClose = () => {
        setExportModalOpen(false);
    };

    return (
        <Box pt={2} pb={4}>
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: (theme) => theme.zIndex.appBar - 1,
                    backgroundColor: "background.default",
                    pt: 0.5,
                    pb: 1,
                    mb: 1,
                }}
            >
                <FlexBetween flexWrap="wrap" mb={2}>
                    <FlexBox alignItems="center" gap={2} style={{ flexGrow: 1 }}>
                    <SearchInput
                        bordered={false}
                        placeholder="Search by farmer name,Contact,village, etc..."
                        onChange={e => {
                            setSearchValue(e.target.value);
                            if (hasSearched && e.target.value !== searchTerm) {
                                setHasSearched(false);
                            }
                        }}
                        value={searchValue}
                        onKeyPress={handleKeyPress}
                        sx={{
                            width: "45%",
                            maxWidth: "none !important"
                        }}
                    />
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<Search />}
                        onClick={handleSearch}
                        disabled={!searchValue.trim() || (searchValue === searchTerm && hasSearched)}
                        sx={{ textTransform: "none", minWidth: 100 }}
                    >
                        Search
                    </Button>
                    </FlexBox>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* ─── CHANGE 3: Export button — disabled when no export_to_excel permission ─ */}
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<GetApp />}
                            onClick={() => can('export_to_excel') && handleExportClick()}
                            disabled={!can('export_to_excel')}
                            sx={{ textTransform: 'none' }}
                        >
                            Export to Excel
                        </Button>
                        {/* ─────────────────────────────────────────────────────────────────────── */}
                    </Box>
                </FlexBetween>

                {!isCompanyUser && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 2,
                            mb: 2,
                        }}
                    >
                        <Typography variant="body2" fontWeight={600}>
                            Filter by category:
                        </Typography>

                        <TextField
                            select
                            size="small"
                            label="All Category"
                            value={companyFilter}
                            onChange={handleCompanyFilterChange}
                            disabled={loading || loadingCompanies}
                            sx={{ minWidth: { xs: "100%", sm: 220 } }}
                        >
                            <MenuItem value="">All Company</MenuItem>
                            {loadingCompanies ? (
                                <MenuItem disabled>
                                    <CircularProgress size={16} sx={{ mr: 1 }} />
                                    Loading companies...
                                </MenuItem>
                            ) : (
                                companyOptions.map((company) => (
                                    <MenuItem key={company.id} value={company.id}>
                                        {company.name}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>

                        {totalVisits !== null && (
                            <Box
                                sx={{
                                    minWidth: { xs: "100%", sm: 220 },
                                    height: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    paddingLeft: "10px",
                                    backgroundColor: (theme) =>
                                        theme.palette.mode === "dark"
                                            ? "rgba(255, 255, 255, 0.05)"
                                            : "rgba(0, 0, 0, 0.02)",
                                    borderRadius: 1,
                                    border: (theme) =>
                                        `1px solid ${
                                            theme.palette.mode === "dark"
                                                ? "rgba(255, 255, 255, 0.23)"
                                                : "rgba(0, 0, 0, 0.23)"
                                        }`,
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    color="text.primary"
                                >
                                    Total Visits: {totalVisits.toLocaleString()}
                                </Typography>
                            </Box>
                        )}

                    </Box>
                )}

                {isCompanyUser && totalVisits !== null && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 2,
                            mb: 2,
                        }}
                    >
                        <Typography variant="body2" fontWeight={600}>
                            Filter by category:
                        </Typography>
                        <Box
                            sx={{
                                position: "relative",
                                minWidth: { xs: "100%", sm: 220 },
                                height: "56px",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-end",
                                px: 1.5,
                                pb: 1.25,
                                pt: 2,
                                backgroundColor: (theme) => theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(0, 0, 0, 0.02)',
                                borderRadius: 1,
                                border: (theme) => `1px solid ${theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.23)'
                                    : 'rgba(0, 0, 0, 0.23)'}`,
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    position: "absolute",
                                    top: "9px",
                                    left: "14px",
                                    fontSize: "0.75rem",
                                    lineHeight: 1,
                                }}
                            >
                                Total Visits
                            </Typography>
                            {loadingDashboard ? (
                                <CircularProgress size={16} />
                            ) : (
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    color="primary.main"
                                    sx={{ lineHeight: 1.4375 }}
                                >
                                    {totalVisits.toLocaleString()}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>

            <Card>
                <Box sx={{ mt: 1 }}>
                    <FieldVisitTable
                        fieldVisits={fieldVisits}
                        searchQuery={searchTerm}
                        loading={loading}
                        onRefresh={() => {
                            const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
                            fetchFieldVisits(targetCompanyId, currentPage, pageSize, searchTerm);
                            fetchDashboardData();
                        }}
                        pagination={pagination}
                        onPageChange={handleFieldVisitPageChange}
                        onPageSizeChange={handleFieldVisitPageSizeChange}
                        pageSizeOptions={pageSizeOptions}
                        // ─── CHANGE 4: Pass view_image permission to table ─────────────────────
                        canViewImage={can('view_image')}
                        // ───────────────────────────────────────────────────────────────────────
                    />
                </Box>
            </Card>

            <FieldVisitExportModal
                open={exportModalOpen}
                onClose={handleExportModalClose}
                onRefresh={() => {
                    const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
                    fetchFieldVisits(targetCompanyId, currentPage, pageSize, searchTerm);
                }}
            />
        </Box>
    );
};

export default FieldVisit;